import React from "react";
import { CheckboxChangeEvent as ICheckboxChangeEvent } from "antd/es/checkbox";
import isEmpty from "lodash/isEmpty";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { Chip } from "@components/chip";
import { internalTokens as staticTokens } from "@constants/tokens";
import { IFilterOption, TErrorMessages } from "../filterModal.types";
import { Checkbox } from "../../checkbox";
import {
    chipFilterItemClass,
    StyledChipFilterWrapper,
    StyledChipGroup,
} from "../filterModal.styles";
import { FILTER_MODAL_DATA_IDS } from "../filterModal.constants";

export interface ICheckboxFiltersProps<
    T extends Record<keyof T, IFilterOption[]>,
> {
    isSingleFilterMode: boolean;
    mode?: "single" | "multiple";
    showSelectAll?: boolean;
    errorMessage?: Partial<TErrorMessages<T>>;
    filterOptions: IFilterOption[];
    selectedFilterType: keyof T;
    selectedFilters: Partial<T>;
    childFilters: (keyof T)[];
    setSelectedFilters: (selectedFilters: Partial<T>) => void;
}

const CheckboxFilters = <T extends Record<keyof T, IFilterOption[]>>(
    props: ICheckboxFiltersProps<T>
) => {
    const {
        isSingleFilterMode,
        mode = "multiple",
        showSelectAll = true,
        filterOptions,
        errorMessage,
        selectedFilters,
        childFilters = [],
        setSelectedFilters,
        selectedFilterType,
    } = props;
    const tokens = useEnigmaToken();
    const isMultiSelectEnabled = mode === "multiple";

    const updateFilterState = (updatedFilterOptions: IFilterOption[]) => {
        const updatedFilters = { ...selectedFilters };
        if (isSingleFilterMode) {
            const newLocalFilters = {
                [selectedFilterType]: updatedFilterOptions,
            } as Partial<T>;
            setSelectedFilters(newLocalFilters);
        } else {
            if (isEmpty(updatedFilterOptions)) {
                delete updatedFilters[selectedFilterType];
                childFilters.forEach(
                    (filterKey) => delete updatedFilters[filterKey]
                );
            } else {
                updatedFilters[selectedFilterType] =
                    updatedFilterOptions as T[keyof T];
            }
            setSelectedFilters(updatedFilters);
        }
    };

    const onSelectAllChange = (ev: ICheckboxChangeEvent) => {
        if (ev.target.checked) {
            updateFilterState(filterOptions.filter((fil) => !fil.isDisabled));
        } else {
            updateFilterState([]);
        }
    };

    const onChipChange = (filterItem: IFilterOption) => {
        const updatedFilters = { ...selectedFilters };
        let updatedFilterOptions: IFilterOption[] =
            updatedFilters[selectedFilterType] || [];

        const _currentFilterIndex = updatedFilterOptions
            ?.map((option) => option.value)
            .indexOf(filterItem.value);

        if (!isMultiSelectEnabled) {
            updatedFilterOptions =
                _currentFilterIndex === -1 ? [filterItem] : [];
        } else if (isMultiSelectEnabled && _currentFilterIndex > -1) {
            updatedFilterOptions.splice(_currentFilterIndex, 1);
        } else if (isMultiSelectEnabled) {
            updatedFilterOptions.push(filterItem);
        }
        updateFilterState(updatedFilterOptions);
    };

    const allOptionsSelected =
        selectedFilters[selectedFilterType]?.length ===
        filterOptions.filter((fil) => !fil.isDisabled)?.length;

    return (
        <StyledChipFilterWrapper
            tokens={tokens}
            hasError={Boolean(errorMessage?.[selectedFilterType])}
        >
            {showSelectAll && isMultiSelectEnabled && (
                <Checkbox
                    value="all"
                    data-testid={FILTER_MODAL_DATA_IDS.CHECKBOX_SELECT_ALL}
                    onChange={onSelectAllChange}
                    checked={allOptionsSelected}
                    style={{
                        marginBottom: staticTokens.spacing.spacing200,
                    }}
                >
                    Select All
                </Checkbox>
            )}
            <StyledChipGroup data-testid={FILTER_MODAL_DATA_IDS.CHECKBOX_GROUP}>
                {filterOptions.map((filterItem) => {
                    const _isSelected = selectedFilters[selectedFilterType]
                        ?.map((opt) => opt.value)
                        ?.includes(filterItem.value);
                    return (
                        <Chip
                            data-testid={FILTER_MODAL_DATA_IDS.CHECKBOX_OPTION}
                            key={filterItem.key}
                            selected={_isSelected}
                            size="large"
                            className={chipFilterItemClass(tokens)}
                            selectableWhenSuffix
                            suffix={
                                <Checkbox
                                    data-testid={
                                        FILTER_MODAL_DATA_IDS.CHECKBOX_INPUT
                                    }
                                    disabled={Boolean(filterItem.isDisabled)}
                                    onClick={() => onChipChange(filterItem)}
                                    checked={_isSelected}
                                />
                            }
                            onClick={() => onChipChange(filterItem)}
                            disabled={Boolean(filterItem.isDisabled)}
                        >
                            {filterItem.title}
                        </Chip>
                    );
                })}
            </StyledChipGroup>
        </StyledChipFilterWrapper>
    );
};

export default CheckboxFilters;
